import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { 
  Send, 
  Users, 
  Mail, 
  MessageSquare, 
  Phone, 
  Clock, 
  CheckCircle2, 
  XCircle, 
  AlertCircle,
  Search,
  Filter,
  Plus,
  Eye,
  ChevronDown,
  PhoneCall,
  PhoneOff,
  MessageCircle,
  Calendar,
  FileText,
  Zap,
  Building,
  User,
  Check,
  Edit,
  Trash2,
  Megaphone,
  AlertTriangle,
  Info,
  Wrench
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { usePermissions } from '@/hooks/usePermissions';
import { api, getApiErrorMessage, massAlertEndpoints, massAlertTemplateEndpoints, objectColorEndpoints, objectIconEndpoints, userEndpoints } from '@/lib/api';
import { PERMISSIONS } from '@/lib/permissions';

const MASS_ALERT_ACTION_PERMISSIONS = {
  create: PERMISSIONS.MASS_ALERT_CREATE,
  read: PERMISSIONS.MASS_ALERT_READ,
  update: PERMISSIONS.MASS_ALERT_UPDATE,
  delete: PERMISSIONS.MASS_ALERT_DELETE,
};

const statusConfig = {
  completed: { color: 'bg-emerald-100 text-emerald-700', label: 'Concluído' },
  sending: { color: 'bg-blue-100 text-blue-700', label: 'A Enviar' },
  scheduled: { color: 'bg-amber-100 text-amber-700', label: 'Agendado' },
  failed: { color: 'bg-red-100 text-red-700', label: 'Falhou' },
};

const responseConfig = {
  confirmed: { color: 'bg-emerald-100 text-emerald-700', label: 'Confirmou', icon: CheckCircle2 },
  callback_requested: { color: 'bg-amber-100 text-amber-700', label: 'Pediu Contacto', icon: PhoneCall },
  no_response: { color: 'bg-slate-100 text-slate-600', label: 'Sem Resposta', icon: PhoneOff },
};

const collectionPayload = (payload, key) => {
  const source = payload?.data ?? payload;
  if (Array.isArray(source)) return source;
  if (Array.isArray(source?.data)) return source.data;
  if (Array.isArray(source?.[key])) return source[key];
  return [];
};

const selectionKey = (id) => String(id ?? '');
const sameId = (a, b) => selectionKey(a) === selectionKey(b);
const toApiId = (id) => {
  const value = selectionKey(id);
  return /^\d+$/.test(value) ? Number(value) : value;
};

const normalizeChannels = (channels) => {
  if (Array.isArray(channels)) return channels.filter(Boolean);
  if (typeof channels === 'string') {
    try {
      const parsed = JSON.parse(channels);
      return normalizeChannels(parsed);
    } catch {
      return channels.split(',').map(channel => channel.trim()).filter(Boolean);
    }
  }
  if (channels && typeof channels === 'object') {
    return Object.entries(channels)
      .filter(([, enabled]) => enabled === true || enabled === 1 || enabled === '1')
      .map(([channel]) => channel);
  }
  return [];
};

const normalizeRecipient = (recipient = {}, index = 0) => ({
  id: recipient.user_id ?? recipient.user?.id ?? recipient.recipient_user_id ?? recipient.id ?? recipient.recipient_id ?? index + 1,
  recipientId: recipient.id ?? recipient.recipient_id ?? null,
  name: recipient.name ?? recipient.user?.name ?? recipient.full_name ?? recipient.email ?? `Destinatario ${index + 1}`,
  status: recipient.status ?? recipient.delivery_status ?? 'pending',
  readAt: recipient.readAt ?? recipient.read_at ?? recipient.opened_at ?? null,
  response: recipient.response ?? recipient.response_status ?? null,
});

const normalizeRecipients = (source) => {
  if (!Array.isArray(source)) return [];
  return source.map(normalizeRecipient);
};

const numberFrom = (...values) => {
  const value = values.find(item => item !== undefined && item !== null);
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeAlert = (alert = {}, fallback = {}) => {
  const recipients = normalizeRecipients(alert.recipients ?? alert.users ?? fallback.recipients);
  const status = alert.status ?? fallback.status ?? 'completed';
  return {
    ...fallback,
    ...alert,
    id: alert.id ?? alert.mass_alert_id ?? alert.alert_id ?? fallback.id,
    uuid: alert.uuid ?? fallback.uuid,
    title: alert.title ?? alert.subject ?? fallback.title ?? '',
    message: alert.message ?? alert.body ?? fallback.message ?? '',
    channels: normalizeChannels(alert.channels ?? alert.channel ?? fallback.channels),
    sentAt: alert.sentAt ?? alert.sent_at ?? alert.scheduled_at ?? alert.created_at ?? fallback.sentAt ?? '',
    totalRecipients: numberFrom(alert.totalRecipients, alert.total_recipients, alert.recipients_count, recipients.length, fallback.totalRecipients),
    delivered: numberFrom(alert.delivered, alert.delivered_count, fallback.delivered),
    read: numberFrom(alert.read, alert.read_count, fallback.read),
    responded: numberFrom(alert.responded, alert.responded_count, alert.responses_count, fallback.responded),
    status,
    template: alert.template?.code ?? alert.template?.id ?? alert.template_code ?? alert.template ?? alert.template_id ?? alert.mass_alert_template_id ?? fallback.template ?? '',
    callScript: alert.callScript ?? alert.call_script ?? fallback.callScript ?? '',
    callConfirmOption: alert.callConfirmOption ?? alert.call_confirm_option ?? fallback.callConfirmOption ?? true,
    callCallbackOption: alert.callCallbackOption ?? alert.call_callback_option ?? fallback.callCallbackOption ?? true,
    scheduledAt: alert.scheduledAt ?? alert.scheduled_at ?? fallback.scheduledAt ?? null,
    recipients,
  };
};

const normalizeAlerts = (payload) =>
  collectionPayload(payload, 'alerts')
    .concat(collectionPayload(payload, 'mass_alerts'))
    .filter((alert, index, alerts) => alerts.indexOf(alert) === index)
    .map((alert, index) => normalizeAlert(alert, {id: index + 1}));

const normalizeUser = (user = {}, index = 0) => {
  const roles = Array.isArray(user.roles)
    ? user.roles.map(role => typeof role === 'string' ? role : role?.name || role?.slug || role?.key).filter(Boolean)
    : [];
  const vehicle = user.resource_reference ?? user.vehicle_reference ?? user.vehicle ?? user.vehicle_name ?? null;
  return {
    id: user.id ?? user.user_id ?? index + 1,
    name: user.name ?? `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() ?? user.email ?? `Utilizador ${index + 1}`,
    email: user.email ?? '',
    phone: user.phone ?? user.phone_number ?? '',
    type: vehicle || roles.includes('condutor') ? 'veiculo' : 'backoffice',
    category: user.department?.name ?? user.department_name ?? user.category ?? user.role ?? 'Sem categoria',
    vehicle,
  };
};

const normalizeUsers = (payload) =>
  collectionPayload(payload, 'users').map(normalizeUser).filter(user => user.id);

const optionId = (source) => source?.id ?? source?.object_color_id ?? source?.object_icon_id ?? '';

const normalizeTemplateOption = (source = {}) => {
  const id = optionId(source);
  const label = source.label || source.name || source.title || String(id);
  return {
    ...source,
    id,
    name: label,
    label,
    color: source.color || source.hex || source.hex_color || source.value || '',
    icon: source.icon || source.icon_name || source.url || source.path || source.image || '',
  };
};

const templateIcon = (template = {}) =>
  template.icon ??
  template.icon_url ??
  template.object_icon?.icon ??
  template.object_icon?.url ??
  template.object_icon?.path ??
  template.object_icon?.image ??
  template.objectIcon?.icon ??
  '';

const isImageIcon = (icon) =>
  typeof icon === 'string' &&
  (/^(https?:|data:image\/|\/)/i.test(icon) || /\.(svg|png|jpe?g|gif|webp)(\?|#|$)/i.test(icon));

const TEMPLATE_LUCIDE_ICONS = {
  AlertCircle,
  AlertTriangle,
  Building,
  Calendar,
  Check,
  CheckCircle2,
  Clock,
  Edit,
  Eye,
  FileText,
  Filter,
  Info,
  Mail,
  Megaphone,
  MessageCircle,
  MessageSquare,
  Phone,
  PhoneCall,
  PhoneOff,
  Plus,
  Search,
  Send,
  Trash2,
  User,
  Users,
  Wrench,
  XCircle,
  Zap,
};

const iconComponentName = (icon) =>
  String(icon || '')
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join('');

const lucideIconFor = (icon) =>
  typeof icon === 'string'
    ? TEMPLATE_LUCIDE_ICONS[icon] || TEMPLATE_LUCIDE_ICONS[iconComponentName(icon)]
    : null;

const templateColor = (template = {}) =>
  template.color ??
  template.hex ??
  template.hex_color ??
  template.icon_color ??
  template.object_color?.color ??
  template.object_color?.hex ??
  template.object_color?.hex_color ??
  template.objectColor?.color ??
  template.objectColor?.hex ??
  template.objectColor?.hex_color ??
  '';

const resolveTemplateIcon = (template = {}, iconById) => {
  const configuredIcon =
    template.icon ??
    template.icon_url ??
    template.object_icon?.icon ??
    template.object_icon?.url ??
    template.object_icon?.path ??
    template.object_icon?.image ??
    template.objectIcon?.icon ??
    '';
  if (configuredIcon) return configuredIcon;
  const option = iconById.get(String(template.object_icon_id ?? template.objectIconId ?? ''));
  return option?.icon || '';
};

const resolveTemplateColor = (template = {}, colorById) => {
  const configuredColor = templateColor(template);
  if (configuredColor) return configuredColor;
  const option = colorById.get(String(template.object_color_id ?? template.objectColorId ?? ''));
  return option?.color || '#64748b';
};

const TemplateIcon = ({icon, color = '#64748b', className = 'text-xl', imageClassName = 'h-6 w-6'}) => {
  if (isImageIcon(icon)) {
    return (
      <span
        className={`${imageClassName} inline-block`}
        style={{
          backgroundColor: color,
          WebkitMask: `url("${icon}") center / contain no-repeat`,
          mask: `url("${icon}") center / contain no-repeat`,
        }}
      />
    );
  }

  if (icon) {
    const IconComponent = lucideIconFor(icon);
    if (IconComponent) return <IconComponent className={imageClassName} style={{color}} />;
    return <span className={className} style={{color}}>{icon}</span>;
  }

  return <Megaphone className={imageClassName} style={{color}} />;
};

const normalizeAlertTemplate = (template = {}, index = 0) => {
  const id = template.code ?? template.id ?? template.uuid ?? index + 1;
  return {
    ...template,
    id: String(id),
    templateId: template.id ?? template.mass_alert_template_id ?? null,
    code: template.code ?? String(id),
    name: template.name ?? template.label ?? `Modelo ${index + 1}`,
    icon: templateIcon(template),
    color: templateColor(template),
    object_color_id: template.object_color_id ?? template.objectColorId ?? template.object_color?.id ?? template.objectColor?.id ?? null,
    object_icon_id: template.object_icon_id ?? template.objectIconId ?? template.object_icon?.id ?? template.objectIcon?.id ?? null,
    subject: template.default_subject ?? template.defaultSubject ?? template.subject ?? '',
    message: template.default_message ?? template.defaultMessage ?? template.message ?? '',
    callScript: template.call_script ?? template.callScript ?? '',
    order_no: Number(template.order_no ?? template.orderNo ?? index),
    status: Number(template.status ?? 1),
  };
};

const normalizeAlertTemplates = (payload) => {
  const templates = collectionPayload(payload, 'templates');
  const massAlertTemplates = templates.length ? templates : collectionPayload(payload, 'mass_alert_templates');
  return massAlertTemplates
    .map(normalizeAlertTemplate)
    .filter(template => Number(template.status) === 1)
    .sort((a, b) => a.order_no - b.order_no || a.name.localeCompare(b.name));
};

const findAlertTemplate = (templates, id) =>
  templates.find(template =>
    sameId(template.id, id) ||
    sameId(template.code, id) ||
    sameId(template.templateId, id)
  );

export const MassAlerts = () => {
  const { can } = usePermissions();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [editingAlert, setEditingAlert] = useState(null);
  const [alertHistory, setAlertHistory] = useState([]);
  const [users, setUsers] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(true);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [alertsError, setAlertsError] = useState('');
  const [usersError, setUsersError] = useState('');
  const [templatesError, setTemplatesError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [alertTemplates, setAlertTemplates] = useState([]);
  const [templateColors, setTemplateColors] = useState([]);
  const [templateIcons, setTemplateIcons] = useState([]);
  
  // Form states
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [alertTitle, setAlertTitle] = useState('');
  const [alertMessage, setAlertMessage] = useState('');
  const [callScript, setCallScript] = useState('');
  const [useEmail, setUseEmail] = useState(true);
  const [useSMS, setUseSMS] = useState(false);
  const [useCall, setUseCall] = useState(false);
  const [callConfirmOption, setCallConfirmOption] = useState(true);
  const [callCallbackOption, setCallCallbackOption] = useState(true);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [scheduleAlert, setScheduleAlert] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const alertPermissions = {
    create: can(MASS_ALERT_ACTION_PERMISSIONS.create),
    read: can(MASS_ALERT_ACTION_PERMISSIONS.read),
    update: can(MASS_ALERT_ACTION_PERMISSIONS.update),
    delete: can(MASS_ALERT_ACTION_PERMISSIONS.delete),
  };
  const canReadAlerts = alertPermissions.read;
  const canManageAlertForm = alertPermissions.create || alertPermissions.update;
  const templateColorById = useMemo(
    () => new Map(templateColors.map(color => [String(color.id), color])),
    [templateColors]
  );
  const templateIconById = useMemo(
    () => new Map(templateIcons.map(icon => [String(icon.id), icon])),
    [templateIcons]
  );
  const templateIconProps = (template) => ({
    icon: resolveTemplateIcon(template, templateIconById),
    color: resolveTemplateColor(template, templateColorById),
  });

  useEffect(() => {
    let cancelled = false;

    const loadAlerts = async () => {
      if (!canReadAlerts) {
        setAlertHistory([]);
        setAlertsError('Nao tem permissao para consultar alertas.');
        setAlertsLoading(false);
        return;
      }

      setAlertsLoading(true);
      setAlertsError('');

      try {
        const response = await api.get(massAlertEndpoints.list);
        if (!cancelled) setAlertHistory(normalizeAlerts(response.data));
      } catch (error) {
        if (!cancelled) {
          setAlertHistory([]);
          setAlertsError(getApiErrorMessage(error, 'Nao foi possivel carregar os alertas.'));
        }
      } finally {
        if (!cancelled) setAlertsLoading(false);
      }
    };

    loadAlerts();

    return () => {
      cancelled = true;
    };
  }, [canReadAlerts]);

  useEffect(() => {
    let cancelled = false;

    const loadTemplates = async () => {
      if (!canReadAlerts) {
        setAlertTemplates([]);
        setTemplateColors([]);
        setTemplateIcons([]);
        setTemplatesError('');
        setTemplatesLoading(false);
        return;
      }

      setTemplatesLoading(true);
      setTemplatesError('');

      try {
        const [templatesResponse, colorsResponse, iconsResponse] = await Promise.all([
          api.get(massAlertTemplateEndpoints.list),
          api.get(objectColorEndpoints.list).catch(() => ({data: []})),
          api.get(objectIconEndpoints.list).catch(() => ({data: []})),
        ]);
        const nextTemplates = normalizeAlertTemplates(templatesResponse.data);
        if (!cancelled) {
          setAlertTemplates(nextTemplates);
          setTemplateColors(collectionPayload(colorsResponse.data, 'colors').map(normalizeTemplateOption).filter(option => option.id));
          setTemplateIcons(collectionPayload(iconsResponse.data, 'icons').map(normalizeTemplateOption).filter(option => option.id));
        }
      } catch (error) {
        if (!cancelled) {
          setAlertTemplates([]);
          setTemplateColors([]);
          setTemplateIcons([]);
          setTemplatesError(getApiErrorMessage(error, 'Nao foi possivel carregar os modelos de alertas.'));
        }
      } finally {
        if (!cancelled) setTemplatesLoading(false);
      }
    };

    loadTemplates();

    return () => {
      cancelled = true;
    };
  }, [canReadAlerts]);

  useEffect(() => {
    let cancelled = false;

    const loadUsers = async () => {
      if (!canManageAlertForm) {
        setUsers([]);
        setUsersError('');
        setUsersLoading(false);
        return;
      }

      setUsersLoading(true);
      setUsersError('');

      try {
        const response = await api.get(userEndpoints.list);
        if (!cancelled) setUsers(normalizeUsers(response.data));
      } catch (error) {
        if (!cancelled) {
          setUsers([]);
          setUsersError(getApiErrorMessage(error, 'Nao foi possível carregar os destinatários.'));
        }
      } finally {
        if (!cancelled) setUsersLoading(false);
      }
    };

    loadUsers();

    return () => {
      cancelled = true;
    };
  }, [canManageAlertForm]);

  // Reset form
  const resetForm = () => {
    setEditingAlert(null);
    setSelectedTemplate('');
    setAlertTitle('');
    setAlertMessage('');
    setCallScript('');
    setUseEmail(true);
    setUseSMS(false);
    setUseCall(false);
    setCallConfirmOption(true);
    setCallCallbackOption(true);
    setSelectedUsers([]);
    setFilterCategory('all');
    setFilterType('all');
    setScheduleAlert(false);
    setScheduleDate('');
    setScheduleTime('');
  };

  const fillFormFromAlert = (alert) => {
    setEditingAlert(alert);
    setSelectedTemplate(alert.template || 'custom');
    setAlertTitle(alert.title || '');
    setAlertMessage(alert.message || '');
    setCallScript(alert.callScript || '');
    setUseEmail(alert.channels.includes('email'));
    setUseSMS(alert.channels.includes('sms'));
    setUseCall(alert.channels.includes('call'));
    setCallConfirmOption(alert.callConfirmOption ?? true);
    setCallCallbackOption(alert.callCallbackOption ?? true);
    setSelectedUsers(alert.recipients.map(recipient => selectionKey(recipient.id)).filter(Boolean));
    setFilterCategory('all');
    setFilterType('all');
    setSearchTerm('');
    const scheduled = alert.status === 'scheduled' || Boolean(alert.scheduledAt);
    const scheduledAt = alert.scheduledAt || alert.sentAt || '';
    const [date = '', time = ''] = String(scheduledAt).replace('T', ' ').split(' ');
    setScheduleAlert(scheduled);
    setScheduleDate(scheduled ? date : '');
    setScheduleTime(scheduled ? time.slice(0, 5) : '');
  };

  const openCreate = (templateId = '') => {
    if (!alertPermissions.create) {
      toast.error('Nao tem permissao para criar alertas.');
      return;
    }

    resetForm();
    if (templateId) handleTemplateSelect(templateId);
    setIsCreateDialogOpen(true);
  };

  const openEdit = (alert) => {
    if (!alertPermissions.update) {
      toast.error('Nao tem permissao para editar alertas.');
      return;
    }

    fillFormFromAlert(alert);
    setIsCreateDialogOpen(true);
  };

  // Handle template selection
  const handleTemplateSelect = (templateId) => {
    setSelectedTemplate(templateId);
    const template = findAlertTemplate(alertTemplates, templateId);
    if (template) {
      setAlertTitle(template.subject);
      setAlertMessage(template.message);
      setCallScript(template.callScript);
    }
  };

  // Filter users
  const filteredUsers = users.filter(user => {
    const matchesCategory = filterCategory === 'all' || user.category === filterCategory;
    const matchesType = filterType === 'all' || user.type === filterType;
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesType && matchesSearch;
  });

  const userCategories = Array.from(new Set(users.map(user => user.category).filter(Boolean)));
  const isUserSelected = (userId) => selectedUsers.some(selectedId => sameId(selectedId, userId));

  // Toggle user selection
  const toggleUserSelection = (userId) => {
    const key = selectionKey(userId);
    setSelectedUsers(prev =>
      prev.some(id => sameId(id, key))
        ? prev.filter(id => !sameId(id, key))
        : [...prev, key]
    );
  };

  const setUserSelection = (userId, checked) => {
    const key = selectionKey(userId);
    setSelectedUsers(prev => {
      if (checked) return prev.some(id => sameId(id, key)) ? prev : [...prev, key];
      return prev.filter(id => !sameId(id, key));
    });
  };

  // Select all filtered users
  const selectAllUsers = () => {
    const allFilteredIds = filteredUsers.map(u => selectionKey(u.id)).filter(Boolean);
    const allSelected = allFilteredIds.every(id => selectedUsers.some(selectedId => sameId(selectedId, id)));
    
    if (allSelected) {
      setSelectedUsers(prev => prev.filter(id => !allFilteredIds.some(filteredId => sameId(filteredId, id))));
    } else {
      setSelectedUsers(prev => [...new Set([...prev, ...allFilteredIds])]);
    }
  };

  const buildAlertPayload = (channels) => {
    const selectedTemplateObj = findAlertTemplate(alertTemplates, selectedTemplate);
    const payload = {
      title: alertTitle.trim(),
      subject: alertTitle.trim(),
      message: alertMessage.trim(),
      body: alertMessage.trim(),
      channels,
      call_script: callScript.trim() || null,
      call_confirm_option: callConfirmOption,
      call_callback_option: callCallbackOption,
      schedule: scheduleAlert,
      scheduled_at: scheduleAlert ? `${scheduleDate} ${scheduleTime}` : null,
    recipient_ids: selectedUsers.map(toApiId),
    user_ids: selectedUsers.map(toApiId),
    };

    if (selectedTemplateObj?.templateId) {
      payload.template = selectedTemplateObj.code;
      payload.template_code = selectedTemplateObj.code;
      payload.mass_alert_template_id = selectedTemplateObj.templateId;
    }

    return payload;
  };

  // Store/update alert
  const handleSendAlert = async () => {
    if (editingAlert && !alertPermissions.update) {
      toast.error('Nao tem permissão para editar alertas.');
      return;
    }

    if (!editingAlert && !alertPermissions.create) {
      toast.error('Nao tem permissão para criar alertas.');
      return;
    }

    if (selectedUsers.length === 0) {
      toast.error('Selecione pelo menos um destinatário');
      return;
    }
    if (!alertTitle || !alertMessage) {
      toast.error('Preencha o título e a mensagem');
      return;
    }

    const channels = [];
    if (useEmail) channels.push('email');
    if (useSMS) channels.push('sms');
    if (useCall) channels.push('call');

    if (channels.length === 0) {
      toast.error('Selecione pelo menos um canal de envio');
      return;
    }

    if (scheduleAlert && (!scheduleDate || !scheduleTime)) {
      toast.error('Preencha a data e hora do agendamento');
      return;
    }

    setSubmitting(true);
    try {
      const payload = buildAlertPayload(channels);
      const fallback = {
        id: editingAlert?.id ?? Date.now(),
        uuid: editingAlert?.uuid,
        title: alertTitle,
        message: alertMessage,
        channels,
        sentAt: scheduleAlert ? `${scheduleDate} ${scheduleTime}` : new Date().toLocaleString('pt-PT'),
        totalRecipients: selectedUsers.length,
        delivered: scheduleAlert ? 0 : selectedUsers.length,
        read: editingAlert?.read ?? 0,
        responded: editingAlert?.responded ?? 0,
        status: scheduleAlert ? 'scheduled' : 'completed',
        template: selectedTemplate || 'custom',
        callScript,
        callConfirmOption,
        callCallbackOption,
        scheduledAt: scheduleAlert ? `${scheduleDate} ${scheduleTime}` : null,
        recipients: selectedUsers.map(id => {
          const user = users.find(item => sameId(item.id, id));
          return {
            id,
            name: user?.name,
            status: scheduleAlert ? 'pending' : 'delivered',
            readAt: null,
            response: null
          };
        })
      };

      if (editingAlert) {
        const response = await api.post(massAlertEndpoints.update, {
          id: editingAlert.id,
          uuid: editingAlert.uuid,
          ...payload,
        });
        const updated = normalizeAlert(response.data?.data ?? response.data?.alert ?? response.data, fallback);
        setAlertHistory(prev => prev.map(alert => alert.id === editingAlert.id ? updated : alert));
        setSelectedAlert(current => current?.id === editingAlert.id ? updated : current);
        toast.success('Alerta atualizado com sucesso.');
      } else {
        const response = await api.post(massAlertEndpoints.create, payload);
        const created = normalizeAlert(response.data?.data ?? response.data?.alert ?? response.data, fallback);
        setAlertHistory(prev => [created, ...prev]);
        toast.success(scheduleAlert ? `Alerta agendado para ${scheduleDate} as ${scheduleTime}` : `Alerta enviado para ${selectedUsers.length} destinatario(s)!`);
      }

      setIsCreateDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Nao foi possível guardar o alerta.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteAlert = async (alert) => {
    if (!alertPermissions.delete) {
      toast.error('Nao tem permissão para remover alertas.');
      return;
    }

    if (!window.confirm(`Eliminar alerta "${alert.title}"?`)) return;

    setSubmitting(true);
    try {
      await api.post(massAlertEndpoints.delete, {
        id: alert.id,
        uuid: alert.uuid,
      });
      setAlertHistory(prev => prev.filter(item => item.id !== alert.id));
      setSelectedAlert(current => current?.id === alert.id ? null : current);
      if (selectedAlert?.id === alert.id) setIsDetailDialogOpen(false);
      toast.success('Alerta removido.');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Nao foi possível remover o alerta.'));
    } finally {
      setSubmitting(false);
    }
  };

  // View alert details
  const handleViewDetails = (alert) => {
    if (!alertPermissions.read) {
      toast.error('Nao tem permissão para consultar alertas.');
      return;
    }

    setSelectedAlert(alert);
    setIsDetailDialogOpen(true);
  };

  // Stats
  const totalAlerts = alertHistory.length;
  const totalSent = alertHistory.reduce((acc, a) => acc + a.totalRecipients, 0);
  const totalResponded = alertHistory.reduce((acc, a) => acc + a.responded, 0);
  const scheduledAlerts = alertHistory.filter(a => a.status === 'scheduled').length;
  const quickAlertTemplates = alertTemplates.slice(0, 4);

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-slate-900 text-3xl font-secondary font-bold tracking-tight">Alertas em Massa</h1>
            <p className="text-slate-600 font-primary mt-1">Envie alertas para utilizadores da frota</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" asChild>
              <Link to="/mass-alert-templates">
                <Megaphone size={18} className="mr-2" />
                Modelos
              </Link>
            </Button>
            {alertPermissions.create && (
              <Button
                data-testid="create-alert-btn"
                className="bg-blue-600 hover:bg-blue-700"
                onClick={() => openCreate()}
                disabled={alertsLoading || usersLoading || templatesLoading || submitting}
              >
                <Plus size={18} className="mr-2" />
                Novo Alerta
              </Button>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-slate-600 text-sm font-medium">Total Alertas</p>
              <Send className="text-blue-600" size={20} />
            </div>
            <p className="text-3xl font-secondary font-bold text-slate-900">{totalAlerts}</p>
            <p className="text-xs text-slate-500 mt-1">Histórico completo</p>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-slate-600 text-sm font-medium">Mensagens Enviadas</p>
              <MessageCircle className="text-emerald-600" size={20} />
            </div>
            <p className="text-3xl font-secondary font-bold text-slate-900">{totalSent}</p>
            <p className="text-xs text-slate-500 mt-1">Total de destinatários</p>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-slate-600 text-sm font-medium">Respostas</p>
              <CheckCircle2 className="text-purple-600" size={20} />
            </div>
            <p className="text-3xl font-secondary font-bold text-slate-900">{totalResponded}</p>
            <p className="text-xs text-slate-500 mt-1">Confirmações recebidas</p>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-slate-600 text-sm font-medium">Agendados</p>
              <Calendar className="text-amber-600" size={20} />
            </div>
            <p className="text-3xl font-secondary font-bold text-slate-900">{scheduledAlerts}</p>
            <p className="text-xs text-slate-500 mt-1">Envios programados</p>
          </Card>
        </div>

        {alertsError && (
          <Card className="p-4 border-red-200 bg-red-50 text-sm text-red-700">
            {alertsError}
          </Card>
        )}

        {usersError && (
          <Card className="p-4 border-red-200 bg-red-50 text-sm text-red-700">
            {usersError}
          </Card>
        )}

        {templatesError && (
          <Card className="p-4 border-amber-200 bg-amber-50 text-sm text-amber-700">
            {templatesError}
          </Card>
        )}

        {/* Alert History */}
        <Card>
          <div className="p-4 border-b border-slate-200">
            <h2 className="font-secondary font-bold text-slate-900">Histórico de Alertas</h2>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Alerta</TableHead>
                <TableHead>Canais</TableHead>
                <TableHead>Enviado</TableHead>
                <TableHead>Destinatários</TableHead>
                <TableHead>Entregues</TableHead>
                <TableHead>Respostas</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {alertsLoading && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-sm text-slate-500 py-8">
                    A carregar alertas...
                  </TableCell>
                </TableRow>
              )}
              {!alertsLoading && alertHistory.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-sm text-slate-500 py-8">
                    Ainda nao existem alertas.
                  </TableCell>
                </TableRow>
              )}
              {!alertsLoading && alertHistory.map((alert) => (
                <TableRow key={alert.id} className="hover:bg-slate-50">
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="text-2xl">
                        <TemplateIcon {...templateIconProps(findAlertTemplate(alertTemplates, alert.template))} className="text-2xl" imageClassName="h-7 w-7" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{alert.title}</p>
                        <p className="text-xs text-slate-600 truncate max-w-[200px]">{alert.message}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {alert.channels.includes('email') && <Mail size={14} className="text-blue-600" />}
                      {alert.channels.includes('sms') && <MessageSquare size={14} className="text-emerald-600" />}
                      {alert.channels.includes('call') && <Phone size={14} className="text-red-600" />}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-sm text-slate-600">
                      <Clock size={14} />
                      {alert.sentAt}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="font-bold">
                      <Users size={12} className="mr-1" />
                      {alert.totalRecipients}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className="bg-emerald-100 text-emerald-700">
                      {alert.delivered}/{alert.totalRecipients}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className="bg-purple-100 text-purple-700">
                      {alert.responded}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className={(statusConfig[alert.status] || statusConfig.completed).color}>
                      {(statusConfig[alert.status] || statusConfig.completed).label}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleViewDetails(alert)}
                      >
                        <Eye size={14} className="mr-1" />
                        Detalhes
                      </Button>
                      {alertPermissions.update && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEdit(alert)}
                          disabled={submitting}
                        >
                          <Edit size={14} className="mr-1" />
                          Editar
                        </Button>
                      )}
                      {alertPermissions.delete && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteAlert(alert)}
                          disabled={submitting}
                        >
                          <Trash2 size={14} className="mr-1 text-red-500" />
                          Remover
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>

        {/* Quick Actions */}
        {alertPermissions.create && (
        <Card className="p-6 bg-gradient-to-br from-blue-50 to-purple-50 border-blue-200">
          <h3 className="font-secondary font-bold text-slate-900 text-lg mb-4 flex items-center gap-2">
            <Zap className="text-amber-500" size={20} />
            Atalhos Rápidos
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {templatesLoading && (
              <div className="col-span-2 md:col-span-4 text-sm text-slate-500 py-6 text-center">
                A carregar modelos...
              </div>
            )}
            {!templatesLoading && quickAlertTemplates.length === 0 && (
              <div className="col-span-2 md:col-span-4 text-sm text-slate-500 py-6 text-center">
                Sem modelos ativos configurados na API.
              </div>
            )}
            {!templatesLoading && quickAlertTemplates.map((template) => (
              <Button
                key={template.id}
                variant="outline"
                className="h-auto py-4 flex flex-col items-center gap-2 bg-white hover:bg-blue-50 border-blue-200"
                onClick={() => openCreate(template.id)}
                disabled={usersLoading || submitting}
              >
                <TemplateIcon {...templateIconProps(template)} className="text-2xl" imageClassName="h-7 w-7" />
                <span className="text-sm font-medium line-clamp-1">{template.name}</span>
              </Button>
            ))}
          </div>
        </Card>
        )}

        {/* Create Alert Dialog */}
        <Dialog open={isCreateDialogOpen} onOpenChange={(open) => {
          if (open && !canManageAlertForm) {
            toast.error('Nao tem permissao para gerir alertas.');
            return;
          }
          if (!open) resetForm();
          setIsCreateDialogOpen(open);
        }}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Send className="text-blue-600" size={20} />
                {editingAlert ? 'Editar Alerta' : 'Criar Novo Alerta'}
              </DialogTitle>
              <DialogDescription>
                Configure e envie alertas para os utilizadores da frota
              </DialogDescription>
            </DialogHeader>

            <Tabs defaultValue="message" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="message">1. Mensagem</TabsTrigger>
                <TabsTrigger value="channels">2. Canais</TabsTrigger>
                <TabsTrigger value="recipients">3. Destinatários</TabsTrigger>
              </TabsList>

              {/* Tab 1: Message */}
              <TabsContent value="message" className="space-y-4 mt-4">
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium mb-2 block">Selecionar Template</Label>
                    <div className="grid grid-cols-5 gap-2">
                      {templatesLoading && (
                        <div className="col-span-5 rounded-lg border border-slate-200 bg-slate-50 p-4 text-center text-sm text-slate-500">
                          A carregar modelos...
                        </div>
                      )}
                      {!templatesLoading && alertTemplates.length === 0 && (
                        <div className="col-span-5 rounded-lg border border-slate-200 bg-slate-50 p-4 text-center text-sm text-slate-500">
                          Sem modelos ativos configurados na API.
                        </div>
                      )}
                      {!templatesLoading && alertTemplates.map((template) => (
                        <Button
                          key={template.id}
                          variant={findAlertTemplate([template], selectedTemplate) ? 'default' : 'outline'}
                          className={`h-auto py-3 flex flex-col items-center gap-1 ${findAlertTemplate([template], selectedTemplate) ? 'bg-blue-600' : ''}`}
                          onClick={() => handleTemplateSelect(template.id)}
                        >
                          <TemplateIcon {...templateIconProps(template)} />
                          <span className="text-xs">{template.name}</span>
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="alert-title">Título do Alerta *</Label>
                    <Input 
                      id="alert-title" 
                      placeholder="Ex: Manutenção Programada"
                      value={alertTitle}
                      onChange={(e) => setAlertTitle(e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="alert-message">Mensagem (Email/SMS) *</Label>
                    <Textarea 
                      id="alert-message" 
                      placeholder="Escreva a mensagem que será enviada..."
                      rows={4}
                      value={alertMessage}
                      onChange={(e) => setAlertMessage(e.target.value)}
                    />
                    <p className="text-xs text-slate-500">{alertMessage.length} caracteres</p>
                  </div>
                </div>
              </TabsContent>

              {/* Tab 2: Channels */}
              <TabsContent value="channels" className="space-y-4 mt-4">
                <div className="space-y-4">
                  {/* Email */}
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <Mail className="text-blue-600" size={24} />
                        <div>
                          <Label className="text-base font-medium">Email</Label>
                          <p className="text-xs text-slate-600">Enviar por correio eletrónico</p>
                        </div>
                      </div>
                      <Switch checked={useEmail} onCheckedChange={setUseEmail} />
                    </div>
                  </div>

                  {/* SMS */}
                  <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <MessageSquare className="text-emerald-600" size={24} />
                        <div>
                          <Label className="text-base font-medium">SMS</Label>
                          <p className="text-xs text-slate-600">Enviar mensagem de texto</p>
                        </div>
                      </div>
                      <Switch checked={useSMS} onCheckedChange={setUseSMS} />
                    </div>
                    {useSMS && (
                      <div className="mt-3 p-2 bg-white rounded border border-emerald-200">
                        <p className="text-xs text-slate-600">
                          ⚠️ SMS limitado a 160 caracteres. Mensagem atual: {alertMessage.length} caracteres
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Call */}
                  <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <Phone className="text-red-600" size={24} />
                        <div>
                          <Label className="text-base font-medium">Chamada Telefónica</Label>
                          <p className="text-xs text-slate-600">Chamada automática com mensagem de voz</p>
                        </div>
                      </div>
                      <Switch checked={useCall} onCheckedChange={setUseCall} />
                    </div>
                    
                    {useCall && (
                      <div className="mt-3 space-y-3">
                        <div className="space-y-2">
                          <Label htmlFor="call-script">Script da Chamada (TTS)</Label>
                          <Textarea 
                            id="call-script" 
                            placeholder="O que será dito na chamada..."
                            rows={3}
                            value={callScript}
                            onChange={(e) => setCallScript(e.target.value)}
                          />
                        </div>

                        <div className="p-3 bg-white rounded-lg border border-red-200 space-y-2">
                          <p className="text-sm font-medium text-slate-900">Opções Interativas</p>
                          
                          <div className="flex items-center justify-between">
                            <div>
                              <Label className="text-sm">Pressione 1 - Confirmar Recebimento</Label>
                              <p className="text-xs text-slate-600">O utilizador confirma que recebeu o alerta</p>
                            </div>
                            <Switch checked={callConfirmOption} onCheckedChange={setCallConfirmOption} />
                          </div>

                          <div className="flex items-center justify-between">
                            <div>
                              <Label className="text-sm">Pressione 2 - Pedir Contacto</Label>
                              <p className="text-xs text-slate-600">O utilizador solicita ser contactado (gera feedback)</p>
                            </div>
                            <Switch checked={callCallbackOption} onCheckedChange={setCallCallbackOption} />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Schedule */}
                  <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <Calendar className="text-amber-600" size={24} />
                        <div>
                          <Label className="text-base font-medium">Agendar Envio</Label>
                          <p className="text-xs text-slate-600">Programar para envio futuro</p>
                        </div>
                      </div>
                      <Switch checked={scheduleAlert} onCheckedChange={setScheduleAlert} />
                    </div>
                    
                    {scheduleAlert && (
                      <div className="mt-3 grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label htmlFor="schedule-date">Data</Label>
                          <Input 
                            id="schedule-date" 
                            type="date"
                            value={scheduleDate}
                            onChange={(e) => setScheduleDate(e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="schedule-time">Hora</Label>
                          <Input 
                            id="schedule-time" 
                            type="time"
                            value={scheduleTime}
                            onChange={(e) => setScheduleTime(e.target.value)}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* Tab 3: Recipients */}
              <TabsContent value="recipients" className="space-y-4 mt-4">
                <div className="space-y-4">
                  {/* Filters */}
                  <div className="flex gap-3">
                    <div className="flex-1">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
                        <Input 
                          placeholder="Pesquisar utilizadores..." 
                          className="pl-9"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </div>
                    </div>
                    <Select value={filterCategory} onValueChange={setFilterCategory}>
                      <SelectTrigger className="w-[180px]">
                        <Building size={14} className="mr-2" />
                        <SelectValue placeholder="Categoria" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas Categorias</SelectItem>
                        {userCategories.map(category => (
                          <SelectItem key={category} value={category}>{category}</SelectItem>
                        ))}</SelectContent>
                    </Select>
                    <Select value={filterType} onValueChange={setFilterType}>
                      <SelectTrigger className="w-[180px]">
                        <User size={14} className="mr-2" />
                        <SelectValue placeholder="Tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos os Tipos</SelectItem>
                        <SelectItem value="veiculo">Utilizador Veículo</SelectItem>
                        <SelectItem value="backoffice">Backoffice</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Selection summary */}
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-2">
                      <Users className="text-blue-600" size={18} />
                      <span className="font-medium text-blue-900">
                        {selectedUsers.length} utilizador(es) selecionado(s)
                      </span>
                    </div>
                    <Button variant="outline" size="sm" onClick={selectAllUsers} disabled={usersLoading}>
                      {filteredUsers.every(u => isUserSelected(u.id)) ? 'Desselecionar Todos' : 'Selecionar Todos'}
                    </Button>
                  </div>

                  {/* Users list */}
                  <div className="border rounded-lg max-h-[300px] overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[50px]">
                            <Checkbox 
                              checked={filteredUsers.length > 0 && filteredUsers.every(u => isUserSelected(u.id))}
                              onCheckedChange={selectAllUsers}
                            />
                          </TableHead>
                          <TableHead>Nome</TableHead>
                          <TableHead>Contacto</TableHead>
                          <TableHead>Categoria</TableHead>
                          <TableHead>Tipo</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {usersLoading && (
                          <TableRow>
                            <TableCell colSpan={5} className="text-center text-sm text-slate-500 py-8">
                              A carregar destinatarios...
                            </TableCell>
                          </TableRow>
                        )}
                        {!usersLoading && filteredUsers.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={5} className="text-center text-sm text-slate-500 py-8">
                              Sem destinatarios para os filtros selecionados.
                            </TableCell>
                          </TableRow>
                        )}
                        {!usersLoading && filteredUsers.map((user) => (
                          <TableRow 
                            key={user.id} 
                            className={`cursor-pointer ${isUserSelected(user.id) ? 'bg-blue-50' : 'hover:bg-slate-50'}`}
                            onClick={() => toggleUserSelection(user.id)}
                          >
                            <TableCell onClick={(event) => event.stopPropagation()}>
                              <Checkbox 
                                checked={isUserSelected(user.id)}
                                onClick={(event) => event.stopPropagation()}
                                onCheckedChange={(checked) => setUserSelection(user.id, checked === true)}
                              />
                            </TableCell>
                            <TableCell>
                              <div>
                                <p className="font-medium text-slate-900">{user.name}</p>
                                {user.vehicle && <p className="text-xs text-slate-600">🚗 {user.vehicle}</p>}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm">
                                <p className="text-slate-900">{user.email}</p>
                                <p className="text-slate-600">{user.phone}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{user.category}</Badge>
                            </TableCell>
                            <TableCell>
                              <Badge className={user.type === 'veiculo' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}>
                                {user.type === 'veiculo' ? 'Veículo' : 'Backoffice'}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </TabsContent>
            </Tabs>

            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={() => { resetForm(); setIsCreateDialogOpen(false); }}>
                Cancelar
              </Button>
              <Button 
                className="bg-blue-600 hover:bg-blue-700"
                onClick={handleSendAlert}
                disabled={
                  submitting ||
                  usersLoading ||
                  selectedUsers.length === 0 ||
                  !alertTitle ||
                  !alertMessage ||
                  (editingAlert ? !alertPermissions.update : !alertPermissions.create)
                }
              >
                <Send size={16} className="mr-2" />
                {editingAlert ? 'Guardar Alterações' : scheduleAlert ? 'Agendar Alerta' : `Enviar para ${selectedUsers.length} destinatário(s)`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Alert Details Dialog */}
        <Dialog open={isDetailDialogOpen} onOpenChange={setIsDetailDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Eye className="text-blue-600" size={20} />
                Detalhes do Alerta
              </DialogTitle>
            </DialogHeader>

            {selectedAlert && (
              <div className="space-y-6">
                {/* Alert Info */}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="text-3xl">
                      <TemplateIcon {...templateIconProps(findAlertTemplate(alertTemplates, selectedAlert.template))} className="text-3xl" imageClassName="h-9 w-9" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-secondary font-bold text-slate-900 text-lg">{selectedAlert.title}</h3>
                      <p className="text-slate-600 mt-1">{selectedAlert.message}</p>
                      <div className="flex items-center gap-4 mt-3">
                        <div className="flex items-center gap-1 text-sm text-slate-600">
                          <Clock size={14} />
                          {selectedAlert.sentAt}
                        </div>
                        <div className="flex items-center gap-1">
                          {selectedAlert.channels.includes('email') && <Mail size={14} className="text-blue-600" />}
                          {selectedAlert.channels.includes('sms') && <MessageSquare size={14} className="text-emerald-600" />}
                          {selectedAlert.channels.includes('call') && <Phone size={14} className="text-red-600" />}
                        </div>
                        <Badge className={(statusConfig[selectedAlert.status] || statusConfig.completed).color}>
                          {(statusConfig[selectedAlert.status] || statusConfig.completed).label}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-4 gap-4">
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <p className="text-2xl font-bold text-blue-700">{selectedAlert.totalRecipients}</p>
                    <p className="text-xs text-slate-600">Destinatários</p>
                  </div>
                  <div className="text-center p-3 bg-emerald-50 rounded-lg">
                    <p className="text-2xl font-bold text-emerald-700">{selectedAlert.delivered}</p>
                    <p className="text-xs text-slate-600">Entregues</p>
                  </div>
                  <div className="text-center p-3 bg-purple-50 rounded-lg">
                    <p className="text-2xl font-bold text-purple-700">{selectedAlert.read}</p>
                    <p className="text-xs text-slate-600">Lidos</p>
                  </div>
                  <div className="text-center p-3 bg-amber-50 rounded-lg">
                    <p className="text-2xl font-bold text-amber-700">{selectedAlert.responded}</p>
                    <p className="text-xs text-slate-600">Respostas</p>
                  </div>
                </div>

                {/* Recipients List */}
                <div>
                  <h4 className="font-secondary font-bold text-slate-900 mb-3">Destinatários</h4>
                  <div className="border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Nome</TableHead>
                          <TableHead>Estado</TableHead>
                          <TableHead>Lido em</TableHead>
                          <TableHead>Resposta</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedAlert.recipients.map((recipient) => (
                          <TableRow key={recipient.id}>
                            <TableCell className="font-medium">{recipient.name}</TableCell>
                            <TableCell>
                              <Badge className={recipient.status === 'delivered' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}>
                                {recipient.status === 'delivered' ? 'Entregue' : 'Pendente'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm text-slate-600">
                              {recipient.readAt || '-'}
                            </TableCell>
                            <TableCell>
                              {recipient.response ? (
                                <Badge className={responseConfig[recipient.response]?.color || 'bg-slate-100 text-slate-600'}>
                                  {responseConfig[recipient.response]?.label || recipient.response}
                                </Badge>
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};
